import React from 'react';
import './PasswordRequirements.scss';

/**
 * PasswordRequirements component for displaying password validation rules
 * Shows a list of requirements with visual indicators for met/unmet status
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.password - Current password value to validate against
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.id - HTML id for the requirements container
 * @param {string} props.ariaLabel - ARIA label for accessibility
 *
 * @returns {JSX.Element} Password requirements display component
 *
 * @example
 * // Basic usage
 * <PasswordRequirements password="MyPass123!" />
 *
 * @example
 * // With custom props
 * <PasswordRequirements
 *   password={password}
 *   id="signup-password-requirements"
 *   className="custom-requirements"
 *   ariaLabel="Password requirements for signup"
 * />
 */
export const PasswordRequirements = ({
  password = '',
  className = '',
  id = 'password-requirements',
  ariaLabel = 'Password requirements',
}) => {
  // Password requirements configuration
  const passwordRequirements = [
    { text: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { text: 'One uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { text: 'One lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { text: 'One number', test: (pwd) => /\d/.test(pwd) },
    {
      text: 'One special character (!@#$%^&*)',
      test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    },
  ];

  // Check if all requirements are met
  const allRequirementsMet = passwordRequirements.every((req) =>
    req.test(password)
  );

  return (
    <div
      id={id}
      className={`password-requirements ${
        allRequirementsMet ? 'all-met' : ''
      } ${className}`}
      role='group'
      aria-label={ariaLabel}
    >
      <p className='requirements-title'>Password must contain:</p>
      <ul className='requirements-list'>
        {passwordRequirements.map((req, index) => (
          <li
            key={index}
            className={`requirement-item ${
              req.test(password) ? 'met' : 'unmet'
            }`}
            aria-label={`${req.text}: ${
              req.test(password) ? 'Requirement met' : 'Requirement not met'
            }`}
          >
            <span className='requirement-icon' aria-hidden='true'>
              {req.test(password) ? '✔' : '○'}
            </span>
            {req.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordRequirements;
